import type {
  ForecastFilter,
  ForecastPort,
  ForecastUtilityFilter,
  ForecastWatchFilter,
} from '../consumer/forecasting';
import {
  parseForecastDetail,
  parseForecastPage,
  parseForecastSummary,
  parseForecastUtility,
  parseForecastWatchPage,
} from './forecasting-wire';
import { createMonitoringRequest, MonitoringReadError } from './http';
export const FORECAST_ERROR_STATUS = {
  MALFORMED_REQUEST: 400,
  INVALID_FORECAST_QUERY: 400,
  FORECAST_NOT_FOUND: 404,
  STORAGE_FAILURE: 503,
} as const;
export type ForecastErrorCode = keyof typeof FORECAST_ERROR_STATUS | 'REQUEST_REJECTED';
export class ForecastRequestError extends MonitoringReadError {
  constructor(
    readonly status: number,
    readonly code: ForecastErrorCode,
  ) {
    super('HTTP');
  }
}
export function parseForecastFailure(status: number, value: unknown): MonitoringReadError {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    return new MonitoringReadError('WIRE');
  const v = value as Record<string, unknown>;
  if (Object.keys(v).length !== 2 || typeof v.error !== 'string' || typeof v.message !== 'string')
    return new MonitoringReadError('WIRE');
  const code = v.error as ForecastErrorCode;
  return (
    code === 'REQUEST_REJECTED'
      ? [403, 405].includes(status)
      : Object.hasOwn(FORECAST_ERROR_STATUS, code) && FORECAST_ERROR_STATUS[code] === status
  )
    ? new ForecastRequestError(status, code)
    : new MonitoringReadError('WIRE');
}
function range(from?: string, to?: string) {
  for (const t of [from, to])
    if (
      t &&
      (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(t) ||
        !Number.isFinite(Date.parse(t)))
    )
      throw new Error('Invalid forecast time');
  if (
    from &&
    to &&
    (Date.parse(from) > Date.parse(to) || Date.parse(to) - Date.parse(from) > 86400000)
  )
    throw new Error('Invalid forecast range');
}
export function forecastQuery(f: ForecastFilter): string {
  for (const id of [f.areaId, f.sourceId])
    if (id && !/^[a-z][a-z0-9-]{0,63}$/.test(id)) throw new Error('Invalid forecast scope');
  if (
    !['', 'TEMPERATURE', 'PARTICULATE'].includes(f.metric) ||
    !['', 'degC', 'ug/m3'].includes(f.unit) ||
    (f.metric && f.unit && f.unit !== (f.metric === 'TEMPERATURE' ? 'degC' : 'ug/m3')) ||
    !Number.isSafeInteger(f.offset) ||
    f.offset < 0 ||
    f.offset > 1000000 ||
    !Number.isSafeInteger(f.limit) ||
    f.limit < 1 ||
    f.limit > 100
  )
    throw new Error('Invalid forecast filter');
  range(f.from, f.to);
  const p = new URLSearchParams();
  for (const key of ['areaId', 'sourceId', 'metric', 'unit', 'from', 'to'] as const)
    if (f[key]) p.set(key, f[key]);
  p.set('offset', String(f.offset));
  p.set('limit', String(f.limit));
  return p.toString();
}
export function forecastUtilityQuery(f: ForecastUtilityFilter): string {
  if (
    (f.metric && !['TEMPERATURE', 'PARTICULATE'].includes(f.metric)) ||
    (f.horizonSeconds !== undefined && ![5, 10, 20].includes(f.horizonSeconds)) ||
    (f.profileVersion && f.profileVersion !== 'FORECAST_SYNTHETIC_V1') ||
    (f.algorithmVersion && !['LOCAL_LINEAR_V1', 'LAST_VALUE_V1'].includes(f.algorithmVersion))
  )
    throw new Error('Invalid forecast utility filter');
  range(f.from, f.to);
  const p = new URLSearchParams();
  for (const [key, value] of Object.entries(f))
    if (value !== undefined && value !== '') p.set(key, String(value));
  return p.toString();
}
export function createForecastAdapter(options: {
  readonly baseOrigin: string;
  readonly fetchImpl?: typeof fetch;
}): ForecastPort {
  const request = createMonitoringRequest(options);
  const root = '/read-models/normal-monitoring/forecasting';
  return {
    watches: async (filter, signal) => {
      const page = await request(
        `${root}/watches?${forecastWatchQuery(filter)}`,
        parseForecastWatchPage,
        signal,
        undefined,
        parseForecastFailure,
      );
      if (
        page.offset !== filter.offset ||
        page.limit !== filter.limit ||
        page.items.some(
          (r) =>
            (filter.areaId && r.watch.areaId !== filter.areaId) ||
            (filter.forecastId && !r.watch.forecastIds.includes(filter.forecastId)),
        )
      )
        throw new MonitoringReadError('WIRE');
      return page;
    },
    summary: (signal) =>
      request(`${root}/summary`, parseForecastSummary, signal, undefined, parseForecastFailure),
    forecasts: async (filter, signal) => {
      const p = await request(
        `${root}/forecasts?${forecastQuery(filter)}`,
        parseForecastPage,
        signal,
        undefined,
        parseForecastFailure,
      );
      if (
        p.offset !== filter.offset ||
        p.limit !== filter.limit ||
        p.items.some(
          (f) =>
            (filter.areaId && f.areaId !== filter.areaId) ||
            (filter.sourceId && f.sourceId !== filter.sourceId) ||
            (filter.metric && f.metric !== filter.metric) ||
            (filter.unit && f.unit !== filter.unit),
        )
      )
        throw new MonitoringReadError('WIRE');
      return p;
    },
    detail: async (id, signal) => {
      if (!/^forecast-[a-f0-9]{64}$/.test(id)) throw new Error('Invalid forecast identity');
      const d = await request(
        `${root}/forecasts/${id}`,
        parseForecastDetail,
        signal,
        undefined,
        parseForecastFailure,
      );
      if (d.forecast.forecastId !== id) throw new MonitoringReadError('WIRE');
      return d;
    },
    utility: (filter, signal) =>
      request(
        `${root}/utility?${forecastUtilityQuery(filter)}`,
        parseForecastUtility,
        signal,
        undefined,
        parseForecastFailure,
      ),
  };
}
export function forecastWatchQuery(filter: ForecastWatchFilter): string {
  if (
    (filter.areaId && !/^[a-z][a-z0-9-]{0,63}$/.test(filter.areaId)) ||
    (filter.forecastId && !/^forecast-[a-f0-9]{64}$/.test(filter.forecastId)) ||
    !Number.isSafeInteger(filter.offset) ||
    filter.offset < 0 ||
    filter.offset > 1000000 ||
    !Number.isSafeInteger(filter.limit) ||
    filter.limit < 1 ||
    filter.limit > 100
  )
    throw new Error('Invalid forecast watch filter');
  range(filter.from, filter.to);
  const p = new URLSearchParams();
  for (const key of ['areaId', 'from', 'to', 'forecastId'] as const)
    if (filter[key]) p.set(key, filter[key]);
  p.set('offset', String(filter.offset));
  p.set('limit', String(filter.limit));
  return p.toString();
}
